import { useEffect, useMemo } from "react"

import { Spin } from "antd"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import EmptyComponent from "../../../Components/Empty"
import Table from "../../../Components/Table"
import { skeletonColumns, skeletonRows } from "../../../Components/Table/skeleton"
import { RoutesPaths } from "../../../constants/user-routes"
import { useOpenDeflectPage } from "../hooks/useDeflectFilters"
import { DEFLECT_SECTION } from "../redux/deflectAnalytics.constants"
import {
  selectDeflectFetching,
  selectDeflectFirstLoad,
  selectDeflectSection
} from "../redux/deflectAnalytics.slice"
import { RepeatCardholdersList } from "./RepeatCardholdersList/RepeatCardholdersList"
import { REPEAT_SUMMARY_CELLS, REPEAT_TABLE_SCROLL, repeatColumns } from "./repeatColumns"
import Section from "./Section"
import SummaryRows from "./SummaryRows/SummaryRows"

// How many the card lists; the rest live on the full page behind "View all".
const VISIBLE = 5

// Cards that generated more than one bank inquiry in the window, ranked by lookup count then
// order value — a leaderboard like Health's Chargeback Olympics.
const RepeatCardholdersPanel = ({ getData }) => {
  const { t } = useTranslation()
  const openPage = useOpenDeflectPage()
  const isMobile = useSelector((s) => s.nav.isMobile)
  const repeat = useSelector(selectDeflectSection(DEFLECT_SECTION.REPEAT_CARDHOLDERS))
  const fetching = useSelector(selectDeflectFetching(DEFLECT_SECTION.REPEAT_CARDHOLDERS))
  const firstLoad = useSelector(selectDeflectFirstLoad(DEFLECT_SECTION.REPEAT_CARDHOLDERS))
  const refetching = fetching && !firstLoad
  const columns = useMemo(() => repeatColumns({ t }), [t])
  const loadingColumns = useMemo(() => skeletonColumns(columns), [columns])
  const rows = repeat?.results ?? []

  useEffect(() => {
    getData(DEFLECT_SECTION.REPEAT_CARDHOLDERS, { page_size: VISIBLE })
  }, [getData])

  return (
    <Section
      title={t("Repeat cardholders")}
      tooltip={t(
        "Cards that generated more than one bank inquiry in the window, ranked by lookup count then order value. Card numbers are masked; the full PAN is never shown."
      )}
      onViewAll={repeat?.count ? () => openPage(RoutesPaths.deflect.analytics_repeat) : undefined}
      autoHeight
    >
      {isMobile ? (
        <Spin spinning={refetching}>
          <RepeatCardholdersList loading={firstLoad} data={rows} skeletonCount={VISIBLE} />
        </Spin>
      ) : rows.length || firstLoad ? (
        <Table
          className="chart-table"
          dataSource={firstLoad ? skeletonRows(VISIBLE) : rows}
          columns={firstLoad ? loadingColumns : columns}
          rowKey={firstLoad ? "key" : "sequence_number"}
          loading={refetching}
          withOutPagination
          scroll={REPEAT_TABLE_SCROLL}
          tableLayout="fixed"
          summary={() => (
            <SummaryRows
              summary={rows.length && !firstLoad ? repeat?.summary : null}
              comparisonSummary={repeat?.comparison_summary}
              comparisonLabel={repeat?.comparison_period?.label}
              cells={REPEAT_SUMMARY_CELLS}
            />
          )}
        />
      ) : (
        <Spin spinning={refetching}>
          <EmptyComponent type="no_repeat_cardholders" />
        </Spin>
      )}
    </Section>
  )
}

export default RepeatCardholdersPanel
